/**
 * Webカメラ権限と初期化状態を扱うフックです。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { logError, logWarn } from "../utils/logger";

/** カメラ初期化を待つ上限時間 */
export const CAMERA_INITIALIZATION_TIMEOUT_MS = 12000;

/** カメラ状態 */
export const CAMERA_STATES = Object.freeze({
  IDLE: "idle",
  REQUESTING: "requesting",
  ACTIVE: "active",
  DENIED: "denied",
  UNSUPPORTED: "unsupported",
  TIMEOUT: "timeout",
  ERROR: "error",
});

/** カメラ失敗理由 */
export const CAMERA_FAILURE_REASONS = Object.freeze({
  NONE: "",
  INSECURE_CONTEXT: "insecure-context",
  NO_DEVICE: "no-device",
  IN_USE: "in-use",
  API_UNAVAILABLE: "api-unavailable",
  UNKNOWN: "unknown",
});

/** カメラ状態 */
export type CameraState = {
  state: (typeof CAMERA_STATES)[keyof typeof CAMERA_STATES];
  reason: (typeof CAMERA_FAILURE_REASONS)[keyof typeof CAMERA_FAILURE_REASONS];
  message: string;
  granted: boolean;
  canAskAgain: boolean;
};

/** 初期状態 */
const INITIAL_CAMERA_STATE: CameraState = {
  state: CAMERA_STATES.IDLE,
  reason: CAMERA_FAILURE_REASONS.NONE,
  message: "",
  granted: false,
  canAskAgain: true,
};

/**
 * カメラエラーを画面表示用の状態へ分類する
 * @param error - ブラウザまたはQRライブラリが返したエラー
 * @returns 分類済みカメラ状態
 */
export const classifyCameraError = (error: unknown): CameraState => {
  const errorName =
    error instanceof DOMException || error instanceof Error ? error.name : "";
  const errorMessage =
    error instanceof Error ? error.message : String(error ?? "");

  if (
    errorName === "CameraTimeoutError" ||
    /camera_initialization_timeout/i.test(errorMessage)
  ) {
    return {
      state: CAMERA_STATES.TIMEOUT,
      reason: CAMERA_FAILURE_REASONS.UNKNOWN,
      message:
        "カメラの起動が12秒以内に完了しませんでした。他のアプリを閉じて再試行するか、コードを手入力してください。",
      granted: false,
      canAskAgain: true,
    };
  }

  if (
    typeof window !== "undefined" &&
    !window.isSecureContext &&
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1"
  ) {
    return {
      state: CAMERA_STATES.UNSUPPORTED,
      reason: CAMERA_FAILURE_REASONS.INSECURE_CONTEXT,
      message:
        "HTTPSで開かれていないためカメラを利用できません。安全なHTTPSページで開くか、コードを手入力してください。",
      granted: false,
      canAskAgain: false,
    };
  }

  if (
    errorName === "NotAllowedError" ||
    errorName === "PermissionDeniedError" ||
    /denied|permission/i.test(errorMessage)
  ) {
    return {
      state: CAMERA_STATES.DENIED,
      reason: CAMERA_FAILURE_REASONS.NONE,
      message:
        "カメラの利用が拒否されています。ブラウザのサイト設定でカメラを許可してから再試行してください。",
      granted: false,
      canAskAgain: true,
    };
  }

  if (
    errorName === "NotFoundError" ||
    errorName === "DevicesNotFoundError" ||
    /no camera|device not found|requested device not found/i.test(errorMessage)
  ) {
    return {
      state: CAMERA_STATES.UNSUPPORTED,
      reason: CAMERA_FAILURE_REASONS.NO_DEVICE,
      message:
        "利用できるカメラが見つかりません。この端末にカメラがない場合はコードを手入力してください。",
      granted: false,
      canAskAgain: false,
    };
  }

  if (
    errorName === "NotReadableError" ||
    errorName === "TrackStartError" ||
    errorName === "AbortError" ||
    /could not start|not readable|in use/i.test(errorMessage)
  ) {
    return {
      state: CAMERA_STATES.ERROR,
      reason: CAMERA_FAILURE_REASONS.IN_USE,
      message:
        "カメラを開始できませんでした。他のカメラアプリやタブを閉じてから再試行してください。",
      granted: false,
      canAskAgain: true,
    };
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    return {
      state: CAMERA_STATES.UNSUPPORTED,
      reason: CAMERA_FAILURE_REASONS.API_UNAVAILABLE,
      message:
        "この端末またはブラウザはカメラ読み取りに対応していません。コードを手入力してください。",
      granted: false,
      canAskAgain: false,
    };
  }

  return {
    state: CAMERA_STATES.ERROR,
    reason: CAMERA_FAILURE_REASONS.UNKNOWN,
    message:
      "カメラの起動に失敗しました。再試行するか、コードを手入力してください。",
    granted: false,
    canAskAgain: true,
  };
};

/**
 * MediaStreamの全trackを停止する
 * @param stream - 停止対象stream
 * @returns なし
 */
export const stopMediaStream = (
  stream: MediaStream | null | undefined,
): void => {
  stream?.getTracks().forEach((track) => track.stop());
};

/**
 * カメラ権限を確認する
 * @returns 権限状態と要求処理
 */
const useCameraPermissions = (): [
  CameraState,
  () => Promise<CameraState>,
] => {
  const [cameraState, setCameraState] =
    useState<CameraState>(INITIAL_CAMERA_STATE);
  /** 未完了の権限確認stream */
  const activeStreamRef = useRef<MediaStream | null>(null);
  /** 最新request ID */
  const requestIdRef = useRef(0);

  useEffect(
    () => () => {
      requestIdRef.current += 1;
      stopMediaStream(activeStreamRef.current);
      activeStreamRef.current = null;
    },
    [],
  );

  /**
   * カメラ権限を要求する
   * @returns 更新後のカメラ状態
   */
  const requestPermission = useCallback(async (): Promise<CameraState> => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    stopMediaStream(activeStreamRef.current);
    activeStreamRef.current = null;

    if (!navigator.mediaDevices?.getUserMedia) {
      const unsupportedState = classifyCameraError(
        new Error("CAMERA_API_UNAVAILABLE"),
      );
      setCameraState(unsupportedState);
      return unsupportedState;
    }

    const requestingState: CameraState = {
      ...INITIAL_CAMERA_STATE,
      state: CAMERA_STATES.REQUESTING,
      message: "カメラの許可を確認しています。",
    };
    setCameraState(requestingState);

    /** 上限時間でrejectするtimer */
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    /** timeout後に遅れて取得された映像も停止するための完了フラグ */
    let isRequestFinished = false;

    try {
      const stream = await Promise.race([
        navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
        }).then((stream) => {
          if (isRequestFinished || requestId !== requestIdRef.current) {
            stopMediaStream(stream);
          }
          return stream;
        }),
        new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => {
            const timeoutError = new Error(
              "CAMERA_INITIALIZATION_TIMEOUT",
            );
            timeoutError.name = "CameraTimeoutError";
            reject(timeoutError);
          }, CAMERA_INITIALIZATION_TIMEOUT_MS);
        }),
      ]);

      if (requestId !== requestIdRef.current) {
        stopMediaStream(stream);
        return INITIAL_CAMERA_STATE;
      }

      activeStreamRef.current = stream;
      stopMediaStream(stream);
      activeStreamRef.current = null;

      const activeState: CameraState = {
        state: CAMERA_STATES.ACTIVE,
        reason: CAMERA_FAILURE_REASONS.NONE,
        message: "カメラを利用できます。",
        granted: true,
        canAskAgain: true,
      };
      setCameraState(activeState);
      return activeState;
    } catch (error) {
      const failedState = classifyCameraError(error);
      const logMetadata = {
        cameraState: failedState.state,
        failureReason: failedState.reason,
      };
      if (failedState.reason === CAMERA_FAILURE_REASONS.UNKNOWN) {
        logError("Webカメラ権限の取得に失敗しました。", logMetadata);
      } else {
        logWarn("Webカメラ権限を取得できませんでした。", logMetadata);
      }
      if (requestId === requestIdRef.current) {
        setCameraState(failedState);
      }
      return failedState;
    } finally {
      isRequestFinished = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }, []);

  return [cameraState, requestPermission];
};

export default useCameraPermissions;
